import { useState, useEffect } from "react";
import { Loader2, ArrowUpRight, ArrowDownRight, DollarSign, CreditCard } from "lucide-react";
import Chart from "react-apexcharts";
import { InfoTooltip, GraphExplainer, DataSourceBadge, CHART_3D } from "./DashboardWidgets";
import api from "../../utils/api";
import "./LiabilitiesEquityTab.css";

/* Color tokens - no green/red allowed on this page */
const C = {
  navy: "#1A202C",
  blue: "#4F86F7",
  amber: "#F59E0B",
  orange: "#F5841F",
  violet: "#8B5CF6",       // liabilities series - replaces former red/amber
  sky: "#38BDF8",
  muted: "#6B7491",
  faint: "#9BA4B8",
  bankBlue: "#1B75BA",
  ccNavy: "#282560",
  textPrimary: "#1C2040",
  border: "1px solid #E8EAF0",
};

const CARD = { border: C.border, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" };

/* NaN guard - degrade non-numeric values to 0 with console warning */
function _num(v, field) {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn(`[LiabilitiesEquityTab] non-numeric value for ${field || "currency field"}:`, v);
    }
    return 0;
  }
  return n;
}

function fmt(n) { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(_num(n, "fmt")); }
function fmtFull(n) { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(_num(n, "fmtFull")); }
function fmtK(n) { const v = _num(n, "fmtK"); return v >= 1000 ? `$${(v/1000).toFixed(0)}k` : `$${v}`; }

function LedgerTable({ title, items, total, badge, className }) {
  return (
    <div className={`bg-white rounded-xl p-4 ${className || ""}`} style={CARD}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-bold font-display" style={{ color: C.textPrimary }}>{title}</h3>
          <DataSourceBadge source="quickbooks" />
        </div>
        {badge && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: "#E0F2FE", color: C.bankBlue }}>MTD</span>}
      </div>
      <div className={className === "le-equity-card" ? "le-equity-card-body" : ""}>
        {items.map((item, i) => (
          <div key={i} className="flex items-center justify-between py-2.5" style={{ borderBottom: "1px solid #F3F4F8" }}>
            <span className="text-[11px] font-medium flex-1" style={{ color: C.textPrimary }}>{item.name}</span>
            <div className="flex items-center gap-3">
              <div className="text-center">
                <p className="text-[8px] uppercase" style={{ color: C.faint }}>Current</p>
                <p className="text-[11px] font-bold font-mono" style={{ color: C.textPrimary }}>${item.current}</p>
              </div>
              <div className="text-center">
                <p className="text-[8px] uppercase" style={{ color: C.faint }}>Previous</p>
                <p className="text-[11px] font-bold font-mono" style={{ color: C.textPrimary }}>${item.previous}</p>
              </div>
              <p className="text-sm font-bold font-mono w-24 text-right" style={{ color: C.blue }}>{fmtFull(item.amount)}</p>
            </div>
          </div>
        ))}
        <div className="flex items-center justify-between py-2 mt-1" style={{ borderTop: `2px solid ${C.navy}` }}>
          <span className="text-[11px] font-bold" style={{ color: C.textPrimary }}>Total</span>
          <p className="text-sm font-bold font-mono" style={{ color: C.navy }}>{fmtFull(total)}</p>
        </div>
      </div>
    </div>
  );
}

export default function LiabilitiesEquityTab({ summary, mtdSummary, dateRange }) {
  /* mtdSummary stays locked to month-to-date and never re-fetches on dateRange changes */
  const mtd = mtdSummary || summary;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (dateRange?.start) params.set("start_date", dateRange.start);
    if (dateRange?.end) params.set("end_date", dateRange.end);
    api.get(`/dashboard-data/liabilities-equity?${params.toString()}`).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [dateRange]);

  if (loading || !data) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" style={{ color: C.bankBlue }} /></div>;
  const { summary_cards: sc, liabilities, equity, trending, bottom_summary: bs } = data;

  return (
    <div className="le-page" data-testid="liabilities-equity-tab">

      {/* ---------- Zone T: Top bar ---------- */}
      <div className="le-top-bar">

        {/* Left section: 2 KPI cards (Bank + Credit Card) - both wrapped in a single div */}
        <div className="le-top-kpi-row">
          <div className="bg-white rounded-xl p-3" style={CARD} data-testid="kpi-bank">
            <div className="flex items-center justify-between">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: C.bankBlue }}><DollarSign className="w-4 h-4 text-white" /></div>
              <DataSourceBadge source="quickbooks" />
            </div>
            <p className="text-[10px] mt-1.5" style={{ color: C.muted }}>Balance by Bank Account (Cash)</p>
            <p className="text-base font-bold font-display" style={{ color: "#E5A100" }}>{fmt(mtd?.bank_balance || 32000)}</p>
            <p className="text-[9px] font-mono" style={{ color: C.faint }}>PREV {fmt(mtd?.bank_prev || 38500)}</p>
          </div>
          <div className="bg-white rounded-xl p-3" style={CARD} data-testid="kpi-credit-card">
            <div className="flex items-center justify-between">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: C.ccNavy }}><CreditCard className="w-4 h-4 text-white" /></div>
              <DataSourceBadge source="quickbooks" />
            </div>
            <p className="text-[10px] mt-1.5" style={{ color: C.muted }}>Total Credit Card Balance</p>
            <p className="text-base font-bold font-display" style={{ color: C.blue }}>{fmt(mtd?.credit_card_balance || 32000)}</p>
            <p className="text-[9px] font-mono" style={{ color: C.faint }}>PREV {fmt(mtd?.credit_card_prev || 38500)}</p>
          </div>
        </div>

        {/* Right section: 5 summary metric tiles */}
        <div className="le-summary-tiles">
          {sc.map((c, i) => {
            const isPositive = (c.change ?? 0) >= 0;
            const trendColor = isPositive ? C.blue : C.orange;
            const Arrow = isPositive ? ArrowUpRight : ArrowDownRight;
            return (
              <div key={i} className="bg-white rounded-xl p-3" style={CARD} data-testid={`summary-tile-${i}`}>
                <div className="flex items-center justify-between mb-0.5">
                  <p className="text-[9px] font-medium" style={{ color: C.muted }}>{c.label}</p>
                  <DataSourceBadge source="quickbooks" />
                </div>
                <p className="text-base font-bold font-display mt-0.5" style={{ color: C.textPrimary }}>{fmt(c.value)}</p>
                <div className="flex items-center gap-0.5 mt-0.5">
                  <Arrow className="w-3 h-3" style={{ color: trendColor }} />
                  <span className="text-[9px] font-semibold font-mono" style={{ color: trendColor }}>
                    {c.change > 0 ? "+" : ""}{c.change}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ---------- Zone M: Main content ---------- */}
      <div className="le-main">

        {/* Left column (2fr): Liabilities table + Trending chart */}
        <div className="le-col-left">
          <LedgerTable title="Liabilities" items={liabilities.items} total={liabilities.total} badge className="le-liabilities-card" />

          <div className="bg-white rounded-xl p-4 le-trending-card" style={CARD} data-testid="le-trending">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold font-display" style={{ color: C.textPrimary }}>Liabilities & Equity Trending</h3>
                <InfoTooltip text="Monthly comparison of total Equity vs. total Liabilities. Growing equity relative to liabilities indicates improving solvency." />
                <DataSourceBadge source="quickbooks" />
              </div>
              <GraphExplainer title="L&E Trending" text="Blue line = total equity; purple line = total liabilities. When the blue line grows faster than purple, your company is building net worth." />
            </div>
            <p className="text-[10px] mb-2" style={{ color: C.muted }}>Month-over-Month Analysis (2024)</p>
            <Chart type="area" height={220} options={{
              ...CHART_3D,
              xaxis: { categories: trending.months, labels: { style: { fontSize: "10px", colors: C.faint } } },
              yaxis: { labels: { formatter: v => fmtK(v), style: { fontSize: "11px", colors: C.faint } } },
              colors: [C.blue, C.violet],
              stroke: { curve: "smooth", width: 2.5 },
              fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 100] } },
              legend: { position: "top", fontSize: "11px" },
              markers: { size: 3, strokeWidth: 0 },
            }} series={[
              { name: "Equity", data: trending.equity },
              { name: "Liabilities", data: trending.liabilities },
            ]} />

            <div className="le-trend-kpis">
              <div className="bg-white rounded-xl p-3 text-center" style={CARD} data-testid="trend-kpi-liabilities">
                <p className="text-[10px]" style={{ color: C.muted }}>Current Liabilities</p>
                <p className="text-base font-bold font-display mt-0.5" style={{ color: C.violet }}>{fmtK(bs.current_liabilities.value)}</p>
                <p className="text-[9px] font-semibold font-mono" style={{ color: C.blue }}>+{bs.current_liabilities.change}% YTD</p>
              </div>
              <div className="bg-white rounded-xl p-3 text-center" style={CARD} data-testid="trend-kpi-equity">
                <p className="text-[10px]" style={{ color: C.muted }}>Current Equity</p>
                <p className="text-base font-bold font-display mt-0.5" style={{ color: C.blue }}>{fmtK(bs.current_equity.value)}</p>
                <p className="text-[9px] font-semibold font-mono" style={{ color: C.blue }}>+{bs.current_equity.change}% YTD</p>
              </div>
              <div className="bg-white rounded-xl p-3 text-center" style={CARD} data-testid="trend-kpi-ratio">
                <div className="flex items-center justify-center gap-1">
                  <p className="text-[10px]" style={{ color: C.muted }}>Equity-to-Liability Ratio</p>
                  <InfoTooltip text="Total Equity / Total Liabilities. Above 1.0 = equity exceeds debt." />
                </div>
                <p className="text-base font-bold font-display mt-0.5" style={{ color: C.textPrimary }}>{bs.equity_to_liability_ratio.value}</p>
                <p className="text-[9px] font-semibold" style={{ color: C.sky }}>{bs.equity_to_liability_ratio.label}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right column (3fr): Equity table */}
        <div className="le-col-right">
          <LedgerTable title="Equity" items={equity.items} total={equity.total} badge className="le-equity-card" />
        </div>
      </div>
    </div>
  );
}
